import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ContactForm from "../../components/ContactForm";
import { useTranslation } from "react-i18next";
import logo from "../../assets/didalogo.png";

const Footer = () => {
  const { t, i18n } = useTranslation("footer");
  const lang = (i18n?.language || "ar").startsWith("ar") ? "ar" : "en";

  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background:
            "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
    >
      {/* Main Footer Content */}
      <Box
        sx={{
          py: 4,
          px: 2,
          background:
            "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          color: "#fff",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {/* Brand Section */}
            <Grid item xs={12} md={3}>
              <Box>
                <Box
                  component="img"
                  src={logo}
                  alt="Dida Logo"
                  sx={{
                    height: 50,
                    mb: 1.5,
                    filter: "brightness(0) invert(1)",
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{
                    background:
                      "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    mb: 1.5,
                  }}
                >
                  DIDA Store
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.6,
                    mb: 2,
                    fontSize: "0.875rem",
                  }}
                >
                  {t("about", {
                    defaultValue:
                      lang === "ar"
                        ? "متجر إلكتروني فاخر لأفضل المنتجات عالية الجودة"
                        : "Premium online store for the best quality products",
                  })}
                </Typography>

                {/* Social Media */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1, color: "#d5aa2f", fontSize: "0.875rem" }}
                  >
                    {t("follow_us", { defaultValue: lang === "ar" ? "تابعنا" : "Follow Us" })}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      component="a"
                      href="https://www.facebook.com/yourpage"
                      target="_blank"
                      rel="noopener"
                      aria-label="facebook"
                      size="small"
                      sx={{
                        bgcolor: "rgba(213, 170, 47, 0.1)",
                        color: "#d5aa2f",
                        border: "1px solid rgba(213, 170, 47, 0.2)",
                        "&:hover": {
                          bgcolor: "#d5aa2f",
                          color: "#fff",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(213, 170, 47, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <FacebookIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://www.instagram.com/yourprofile"
                      target="_blank"
                      rel="noopener"
                      aria-label="instagram"
                      size="small"
                      sx={{
                        bgcolor: "rgba(213, 170, 47, 0.1)",
                        color: "#d5aa2f",
                        border: "1px solid rgba(213, 170, 47, 0.2)",
                        "&:hover": {
                          bgcolor: "#d5aa2f",
                          color: "#fff",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(213, 170, 47, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <InstagramIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://twitter.com/yourprofile"
                      target="_blank"
                      rel="noopener"
                      aria-label="twitter"
                      size="small"
                      sx={{
                        bgcolor: "rgba(213, 170, 47, 0.1)",
                        color: "#d5aa2f",
                        border: "1px solid rgba(213, 170, 47, 0.2)",
                        "&:hover": {
                          bgcolor: "#d5aa2f",
                          color: "#fff",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(213, 170, 47, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <TwitterIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://wa.me/yourwhatsappphonenumber"
                      target="_blank"
                      rel="noopener"
                      aria-label="whatsapp"
                      size="small"
                      sx={{
                        bgcolor: "rgba(213, 170, 47, 0.1)",
                        color: "#d5aa2f",
                        border: "1px solid rgba(213, 170, 47, 0.2)",
                        "&:hover": {
                          bgcolor: "#d5aa2f",
                          color: "#fff",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(213, 170, 47, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 2, color: "#d5aa2f" }}
              >
                {t("quick_links", { defaultValue: lang === "ar" ? "روابط سريعة" : "Quick Links" })}
              </Typography>
              <Stack spacing={1}>
                <Link
                  href="/"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {lang === "ar" ? "الرئيسية" : "Home"}
                </Link>
                <Link
                  href="/products"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("products", { defaultValue: lang === "ar" ? "المنتجات" : "Products" })}
                </Link>
                <Link
                  href="/best-sellers"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("best_sellers", { defaultValue: lang === "ar" ? "الأكثر مبيعاً" : "Best Sellers" })}
                </Link>
                <Link
                  href="/new-arrivals"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("new_arrivals", { defaultValue: lang === "ar" ? "وصل حديثاً" : "New Arrivals" })}
                </Link>
                <Link
                  href="/top-rated"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("top_rated", { defaultValue: lang === "ar" ? "الأعلى تقييماً" : "Top Rated" })}
                </Link>
              </Stack>
            </Grid>

            {/* Customer Service */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 2, color: "#d5aa2f" }}
              >
                {t("customer_service", { defaultValue: lang === "ar" ? "خدمة العملاء" : "Customer Service" })}
              </Typography>
              <Stack spacing={1}>
                <Link
                  href="/saved-products"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("saved_products", { defaultValue: lang === "ar" ? "المنتجات المحفوظة" : "Saved Products" })}
                </Link>
                <Link
                  href="/myorders"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("my_orders", { defaultValue: lang === "ar" ? "طلباتي" : "My Orders" })}
                </Link>
                <Link
                  href="/privacy-policy"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("privacy_policy", { defaultValue: lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy" })}
                </Link>
                <Link
                  href="/about-us"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("about_us", { defaultValue: lang === "ar" ? "عن الشركة" : "About Us" })}
                </Link>
                <Link
                  href="/contact-us"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("contact_us_link", { defaultValue: lang === "ar" ? "تواصل معنا" : "Contact Us" })}
                </Link>
                <Link
                  href="/refund-shipping-policy"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("refund_shipping_policy", {
                    defaultValue: lang === "ar" ? "سياسة الاسترجاع والشحن" : "Refund & Shipping Policy",
                  })}
                </Link>
                <Link
                  href="/login"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("login", { defaultValue: lang === "ar" ? "تسجيل الدخول" : "Login" })}
                </Link>
                <Link
                  href="/register"
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "#d5aa2f",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {t("register", { defaultValue: lang === "ar" ? "تسجيل حساب" : "Register" })}
                </Link>
              </Stack>
            </Grid>

            {/* Contact Section - Info & Form Side by Side */}
            <Grid item xs={12} sm={12} md={5}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 2, color: "#d5aa2f" }}
              >
                {t("contact_us", { defaultValue: lang === "ar" ? "تواصل معنا" : "Contact Us" })}
              </Typography>
              
              <Grid container spacing={2}>
                {/* Contact Info */}
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <EmailIcon sx={{ color: "#d5aa2f", fontSize: 18 }} />
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                        info@didastore.com
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <PhoneIcon sx={{ color: "#d5aa2f", fontSize: 18 }} />
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                        +20 123 456 7890
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <LocationOnIcon sx={{ color: "#d5aa2f", fontSize: 18 }} />
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                        {lang === "ar" ? "البحيرة مصر" : "Cairo, Egypt"}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                {/* Contact Form */}
                <Grid item xs={12} sm={6}>
                  <ContactForm />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Bottom Bar */}
      <Box
        sx={{
          py: 2,
          px: 2,
          bgcolor: "#000",
          borderTop: "1px solid rgba(213, 170, 47, 0.1)",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontWeight: 600,
                textAlign: { xs: "center", sm: "left" },
                fontSize: "0.75rem",
              }}
            >
              {t("copyright", {
                year: new Date().getFullYear(),
                defaultValue: `© ${new Date().getFullYear()} DIDA Store. ${
                  lang === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved"
                }.`,
              })}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.3)",
                fontWeight: 600,
                textAlign: { xs: "center", sm: "right" },
                fontSize: "0.7rem",
              }}
            >
              {lang === "ar" 
                ? "مصمم بـ ❤️ من فريق DIDA" 
                : "Designed with ❤️ by DIDA Team"}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
